import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import BusinessLayout from '../../components/layout/BusinessLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { 
  MegaphoneIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlayIcon,
  PauseIcon,
  EnvelopeIcon,
  UserIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import { buildApiUrl } from '../../config/api';

interface Broadcast {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'push';
  subject?: string;
  content: string;
  recipients: number;
  sentCount: number;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed' | 'paused';
  scheduledAt?: string;
  sentAt?: string;
  createdAt: string;
}

interface AudienceFilters {
  venueId?: string;
  activityId?: string;
  yearGroup?: string;
}

const CommunicationsBroadcastsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [broadcastFormData, setBroadcastFormData] = useState<{
    title: string;
    subject: string;
    bodyHtml: string;
    bodyText: string;
    audienceQuery: {
      type: string;
      filters: AudienceFilters;
    };
    channels: string[];
    scheduledFor: string;
    sendNow: boolean;
  }>({
    title: '',
    subject: '',
    bodyHtml: '',
    bodyText: '',
    audienceQuery: {
      type: 'all',
      filters: {}
    },
    channels: ['email'],
    scheduledFor: '',
    sendNow: true
  });
  const [saving, setSaving] = useState(false);
  
  // Individual email state
  const [showIndividualEmail, setShowIndividualEmail] = useState(false);
  const [parentSearchTerm, setParentSearchTerm] = useState('');
  const [parents, setParents] = useState<Array<{id: string, email: string, firstName: string, lastName: string, fullName: string}>>([]);
  const [filteredParents, setFilteredParents] = useState<Array<{id: string, email: string, firstName: string, lastName: string, fullName: string}>>([]);
  const [selectedParent, setSelectedParent] = useState<{id: string, email: string, fullName: string} | null>(null);
  const [emailMode, setEmailMode] = useState<'parent' | 'manual'>('parent');
  const [manualEmailData, setManualEmailData] = useState({
    email: '',
    name: ''
  });
  const [individualEmailData, setIndividualEmailData] = useState({
    subject: '',
    bodyHtml: '',
    bodyText: ''
  });
  const [sendingIndividualEmail, setSendingIndividualEmail] = useState(false);
  
  // Venue and activity filters
  const [venues, setVenues] = useState<Array<{id: string, name: string}>>([]);
  const [activities, setActivities] = useState<Array<{id: string, title: string}>>([]);
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');
  const [selectedActivityId, setSelectedActivityId] = useState<string>('');

  const fetchVenues = useCallback(async () => {
    try {
      const token = authService.getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(buildApiUrl('/business/venues'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setVenues((data.data.venues || []).map((v: any) => ({ id: v.id, name: v.name })));
        }
      }
    } catch (error) {
      console.error('Error fetching venues:', error);
    }
  }, [navigate]);

  const fetchActivities = useCallback(async () => {
    try {
      const token = authService.getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      if (!selectedVenueId) {
        setActivities([]);
        return;
      }

      const queryParams = new URLSearchParams();
      queryParams.append('venue', selectedVenueId);

      const response = await fetch(buildApiUrl(`/business/activities?${queryParams.toString()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setActivities((data.data.activities || []).map((a: any) => ({ id: a.id, title: a.name || a.title })));
        }
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  }, [selectedVenueId, navigate]);

  const fetchParents = useCallback(async () => {
    try {
      const token = authService.getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const queryParams = new URLSearchParams();
      if (selectedVenueId) {
        queryParams.append('venueId', selectedVenueId);
      }
      if (selectedActivityId) {
        queryParams.append('activityId', selectedActivityId);
      }
      if (parentSearchTerm) {
        queryParams.append('search', parentSearchTerm);
      }

      const response = await fetch(buildApiUrl(`/business/communications/parents?${queryParams.toString()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch parents');
      }

      const data = await response.json();
      if (data.success) {
        setParents(data.data.parents || []);
        setFilteredParents(data.data.parents || []);
      } else {
        throw new Error(data.message || 'Failed to fetch parents');
      }
    } catch (error) {
      console.error('Error fetching parents:', error);
      // Don't show error toast as this is called automatically
    }
  }, [selectedVenueId, selectedActivityId, parentSearchTerm, navigate]);

  useEffect(() => {
    fetchBroadcasts();
  }, []);

  // Fetch venues when form opens or when broadcast form needs them
  useEffect(() => {
    if (showIndividualEmail || showCreateForm) {
      fetchVenues();
    }
  }, [showIndividualEmail, showCreateForm, fetchVenues]);

  // Fetch activities when venue is selected
  useEffect(() => {
    if (showIndividualEmail && selectedVenueId) {
      fetchActivities();
    } else if (showIndividualEmail && !selectedVenueId) {
      setActivities([]);
      setSelectedActivityId('');
    }
  }, [selectedVenueId, showIndividualEmail, fetchActivities]);

  // Fetch parents when filters change or form opens
  useEffect(() => {
    if (!showIndividualEmail) return;
    
    // Debounce search term changes
    const timeoutId = setTimeout(() => {
      fetchParents();
    }, parentSearchTerm ? 300 : 0); // 300ms debounce for search, immediate for filter changes
    
    return () => clearTimeout(timeoutId);
  }, [showIndividualEmail, selectedVenueId, selectedActivityId, parentSearchTerm, fetchParents]);

  // Update filtered parents when parents list changes
  useEffect(() => {
    setFilteredParents(parents);
  }, [parents]);

  const fetchBroadcasts = async () => {
    try {
      setLoading(true);
      const token = authService.getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout

      const response = await fetch(buildApiUrl('/business/communications/broadcasts'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to fetch broadcasts');
      }

      const data = await response.json();
      if (data.success) {
        // Transform API data to match our interface
        const transformedBroadcasts: Broadcast[] = (data.data.broadcasts || []).map((broadcast: any) => ({
          id: broadcast.id,
          name: broadcast.name || broadcast.subject || 'Untitled Broadcast',
          type: broadcast.type || 'email',
          subject: broadcast.subject,
          content: broadcast.content || '',
          recipients: broadcast.recipients || 0,
          sentCount: broadcast.sentCount || 0,
          status: broadcast.status || 'draft',
          scheduledAt: broadcast.scheduledAt,
          sentAt: broadcast.sentAt,
          createdAt: broadcast.createdAt
        }));
        setBroadcasts(transformedBroadcasts);
      } else {
        throw new Error(data.message || 'Failed to fetch broadcasts');
      }
    } catch (error) {
      console.error('Error fetching broadcasts:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        toast.error('Broadcasts loading timeout - please refresh');
      } else {
        toast.error('Failed to load broadcasts');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredBroadcasts = broadcasts.filter(broadcast => {
    const matchesSearch = broadcast.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (broadcast.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         broadcast.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || broadcast.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || broadcast.status === statusFilter;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'sending':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'paused':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'scheduled':
        return <ClockIcon className="h-4 w-4" />;
      case 'sending':
        return <PlayIcon className="h-4 w-4" />;
      case 'draft':
        return <PencilIcon className="h-4 w-4" />;
      case 'failed':
        return <XCircleIcon className="h-4 w-4" />;
      case 'paused':
        return <PauseIcon className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const handleDeleteBroadcast = async (broadcastId: string) => {
    if (window.confirm('Are you sure you want to delete this broadcast?')) {
      try {
        // Mock delete - replace with actual API call
        setBroadcasts(prev => prev.filter(b => b.id !== broadcastId));
        toast.success('Broadcast deleted successfully');
      } catch (error) {
        console.error('Error deleting broadcast:', error);
        toast.error('Failed to delete broadcast');
      }
    }
  };

  const handlePauseBroadcast = async (broadcastId: string) => {
    try {
      // Mock pause - replace with actual API call
      setBroadcasts(prev => prev.map(b => 
        b.id === broadcastId ? { ...b, status: b.status === 'sending' ? 'paused' : 'sending' } : b
      ));
      toast.success('Broadcast status updated');
    } catch (error) {
      console.error('Error updating broadcast:', error);
      toast.error('Failed to update broadcast');
    }
  };

  const handleSaveBroadcast = async (broadcastData: any) => {
    try {
      const token = authService.getToken();
      if (!token) {
        toast.error('Please log in to create broadcast');
        return;
      }

      const response = await fetch(buildApiUrl('/business/communications/broadcasts'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(broadcastData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create broadcast');
      }

      const data = await response.json();
      if (data.success) {
        toast.success('Broadcast created successfully!');
        fetchBroadcasts(); // Refresh the list
      } else {
        throw new Error(data.message || 'Failed to create broadcast');
      }
    } catch (error) {
      console.error('Error creating broadcast:', error);
      toast.error('Failed to create broadcast');
    }
  };

  const handleSendIndividualEmail = async () => {
    // Validate based on mode
    if (emailMode === 'parent') {
      if (!selectedParent || !individualEmailData.subject || !individualEmailData.bodyHtml) {
        toast.error('Please select a parent and fill in subject and message');
        return;
      }
    } else {
      if (!manualEmailData.email || !individualEmailData.subject || !individualEmailData.bodyHtml) {
        toast.error('Please fill in email address, subject, and message');
        return;
      }
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(manualEmailData.email)) {
        toast.error('Please enter a valid email address');
        return;
      }
    }

    try {
      setSendingIndividualEmail(true);
      const token = authService.getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const requestBody: any = {
        subject: individualEmailData.subject,
        bodyHtml: individualEmailData.bodyHtml,
        bodyText: individualEmailData.bodyText || individualEmailData.bodyHtml.replace(/<[^>]*>/g, '')
      };

      if (emailMode === 'parent' && selectedParent) {
        requestBody.parentId = selectedParent.id;
      } else {
        requestBody.toEmail = manualEmailData.email;
        requestBody.toName = manualEmailData.name || manualEmailData.email;
      }

      const response = await fetch(buildApiUrl('/business/communications/send-individual'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to send email');
      }

      const data = await response.json();
      if (data.success) {
        const recipientName = emailMode === 'parent' 
          ? selectedParent?.fullName 
          : (manualEmailData.name || manualEmailData.email);
        toast.success(`Email sent successfully to ${recipientName}`);
        setShowIndividualEmail(false);
        setSelectedParent(null);
        setManualEmailData({ email: '', name: '' });
        setIndividualEmailData({
          subject: '',
          bodyHtml: '',
          bodyText: ''
        });
        setParentSearchTerm('');
        setEmailMode('parent');
        fetchBroadcasts(); // Refresh broadcasts to show the new email
      } else {
        throw new Error(data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending individual email:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to send email');
    } finally {
      setSendingIndividualEmail(false);
    }
  };

  if (loading) {
    return (
      <BusinessLayout user={user}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-64 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="bg-white p-6 rounded-lg shadow">
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/2 mb-4"></div>
                  <div className="h-3 bg-gray-300 rounded w-full mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </BusinessLayout>
    );
  }

  return (
    <BusinessLayout user={user}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Broadcasts</h1>
            <p className="text-gray-600 mt-1">Manage your mass communications and campaigns</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => setShowIndividualEmail(true)}
            >
              <EnvelopeIcon className="h-5 w-5" />
              Send Individual Email
            </Button>
            <Button 
              className="flex items-center gap-2"
              onClick={() => setShowCreateForm(true)}
            >
              <PlusIcon className="h-5 w-5" />
              Create Broadcast
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <Card className="p-6">
            <div className="flex items-center">
              <MegaphoneIcon className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Broadcasts</p>
                <p className="text-2xl font-bold text-gray-900">{broadcasts.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Sent</p>
                <p className="text-2xl font-bold text-gray-900">
                  {broadcasts.filter(b => b.status === 'sent').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center">
              <ClockIcon className="h-8 w-8 text-yellow-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Scheduled</p>
                <p className="text-2xl font-bold text-gray-900">
                  {broadcasts.filter(b => b.status === 'scheduled').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center">
              <PencilIcon className="h-8 w-8 text-gray-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Drafts</p>
                <p className="text-2xl font-bold text-gray-900">
                  {broadcasts.filter(b => b.status === 'draft').length}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search broadcasts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00806a] focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00806a] focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="push">Push Notification</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00806a] focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="sent">Sent</option>
                <option value="scheduled">Scheduled</option>
                <option value="sending">Sending</option>
                <option value="draft">Draft</option>
                <option value="failed">Failed</option>
                <option value="paused">Paused</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchTerm('');
                  setTypeFilter('all');
                  setStatusFilter('all');
                }}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </Card>

        {/* Broadcasts List */}
        <div className="space-y-4">
          {filteredBroadcasts.map((broadcast) => (
            <Card key={broadcast.id} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <MegaphoneIcon className="h-6 w-6 text-purple-500" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{broadcast.name}</h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(broadcast.status)}`}>
                          {getStatusIcon(broadcast.status)}
                          <span className="ml-1">{broadcast.status}</span>
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          broadcast.type === 'email' ? 'bg-blue-100 text-blue-800' :
                          broadcast.type === 'sms' ? 'bg-green-100 text-green-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {broadcast.type.toUpperCase()}
                        </span>
                      </div>
                      {broadcast.subject && (
                        <p className="text-sm text-gray-600 mb-1">Subject: {broadcast.subject}</p>
                      )}
                      <p className="text-sm text-gray-600 line-clamp-2">{broadcast.content}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 text-sm text-gray-500">
                    <span>{broadcast.sentCount} / {broadcast.recipients} sent</span>
                    {broadcast.scheduledAt && (
                      <span>Scheduled for {new Date(broadcast.scheduledAt).toLocaleDateString()}</span>
                    )}
                    {broadcast.sentAt && (
                      <span>Sent {new Date(broadcast.sentAt).toLocaleDateString()}</span>
                    )}
                    <span>Created {new Date(broadcast.createdAt).toLocaleDateString()}</span>
                  </div>

                  {broadcast.recipients > 0 && (
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-[#00806a] h-2 rounded-full" 
                          style={{ width: `${(broadcast.sentCount / broadcast.recipients) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button className="p-2 text-gray-400 hover:text-gray-600">
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-blue-600">
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  {(broadcast.status === 'sending' || broadcast.status === 'paused') && (
                    <button 
                      onClick={() => handlePauseBroadcast(broadcast.id)}
                      className="p-2 text-gray-400 hover:text-yellow-600"
                    >
                      {broadcast.status === 'sending' ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
                    </button>
                  )}
                  <button 
                    onClick={() => handleDeleteBroadcast(broadcast.id)}
                    className="p-2 text-gray-400 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {filteredBroadcasts.length === 0 && (
          <Card className="p-12 text-center">
            <MegaphoneIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No broadcasts found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || typeFilter !== 'all' || statusFilter !== 'all' 
                ? 'Try adjusting your search criteria'
                : 'Get started by creating your first broadcast'
              }
            </p>
            <Button onClick={() => setShowCreateForm(true)}>
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Broadcast
            </Button>
          </Card>
        )}

        {/* Create Broadcast Form - Full Page */}
        {showCreateForm && (
          <Card className="p-6 mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Create Broadcast</h2>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false);
                  setBroadcastFormData({
                    title: '',
                    subject: '',
                    bodyHtml: '',
                    bodyText: '',
                    audienceQuery: {
                      type: 'all',
                      filters: {} as AudienceFilters
                    },
                    channels: ['email'],
                    scheduledFor: '',
                    sendNow: true
                  });
                }}
              >
                Cancel
              </Button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  setSaving(true);
                  const token = authService.getToken();
                  if (!token) {
                    toast.error('Please log in to create broadcast');
                    return;
                  }

                  const broadcastData = {
                    title: broadcastFormData.title,
                    subject: broadcastFormData.subject,
                    bodyHtml: broadcastFormData.bodyHtml,
                    bodyText: broadcastFormData.bodyText || broadcastFormData.bodyHtml.replace(/<[^>]*>/g, ''),
                    audienceQuery: broadcastFormData.audienceQuery,
                    channels: broadcastFormData.channels,
                    scheduledFor: broadcastFormData.sendNow ? null : broadcastFormData.scheduledFor
                  };

                  const response = await fetch(buildApiUrl('/business/communications/broadcasts'), {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(broadcastData)
                  });

                  if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || 'Failed to create broadcast');
                  }

                  const data = await response.json();
                  if (data.success) {
                    toast.success('Broadcast created successfully');
                    setShowCreateForm(false);
                    setBroadcastFormData({
                      title: '',
                      subject: '',
                      bodyHtml: '',
                      bodyText: '',
                      audienceQuery: {
                        type: 'all',
                        filters: {}
                      },
                      channels: ['email'],
                      scheduledFor: '',
                      sendNow: true
                    });
                    fetchBroadcasts();
                  } else {
                    throw new Error(data.message || 'Failed to create broadcast');
                  }
                } catch (error) {
                  console.error('Error creating broadcast:', error);
                  toast.error(error instanceof Error ? error.message : 'Failed to create broadcast');
                } finally {
                  setSaving(false);
                }
              }}
              className="space-y-6"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Broadcast Title *
                </label>
                <input
                  type="text"
                  value={broadcastFormData.title}
                  onChange={(e) => setBroadcastFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Summer Camp Announcement"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Subject *
                </label>
                <input
                  type="text"
                  value={broadcastFormData.subject}
                  onChange={(e) => setBroadcastFormData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message Content (HTML) *
                </label>
                <textarea
                  value={broadcastFormData.bodyHtml}
                  onChange={(e) => setBroadcastFormData(prev => ({ ...prev, bodyHtml: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={12}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plain Text Version
                </label>
                <textarea
                  value={broadcastFormData.bodyText}
                  onChange={(e) => setBroadcastFormData(prev => ({ ...prev, bodyText: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={8}
                  placeholder="Optional: Plain text version for email clients that don't support HTML"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Channels *
                </label>
                <div className="space-y-2">
                  {['email', 'sms', 'in_app'].map((channel) => (
                    <label key={channel} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={broadcastFormData.channels.includes(channel)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setBroadcastFormData(prev => ({
                              ...prev,
                              channels: [...prev.channels, channel]
                            }));
                          } else {
                            setBroadcastFormData(prev => ({
                              ...prev,
                              channels: prev.channels.filter(c => c !== channel)
                            }));
                          }
                        }}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700 capitalize">{channel.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audience
                </label>
                <select
                  value={broadcastFormData.audienceQuery.type}
                  onChange={async (e) => {
                    const newType = e.target.value;
                    setBroadcastFormData(prev => ({
                      ...prev,
                      audienceQuery: { 
                        type: newType,
                        filters: newType === 'all' ? {} : prev.audienceQuery.filters
                      }
                    }));
                    // Clear filters when switching away from venue/activity
                    if (newType !== 'venue' && newType !== 'activity') {
                      setBroadcastFormData(prev => ({
                        ...prev,
                        audienceQuery: { ...prev.audienceQuery, filters: {} }
                      }));
                    }
                    // If switching to activity, fetch all activities
                    if (newType === 'activity' && activities.length === 0) {
                      try {
                        const token = authService.getToken();
                        if (!token) return;

                        const response = await fetch(buildApiUrl('/business/activities'), {
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                          }
                        });

                        if (response.ok) {
                          const data = await response.json();
                          if (data.success) {
                            setActivities((data.data.activities || []).map((a: any) => ({ id: a.id, title: a.name || a.title })));
                          }
                        }
                      } catch (error) {
                        console.error('Error fetching activities:', error);
                      }
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="all">All Parents</option>
                  <option value="venue">By Venue</option>
                  <option value="activity">By Activity</option>
                  <option value="year_group">By Year Group</option>
                </select>
              </div>

              {/* Venue Selection */}
              {broadcastFormData.audienceQuery.type === 'venue' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Venue *
                  </label>
                  <select
                    value={broadcastFormData.audienceQuery.filters?.venueId || ''}
                    onChange={(e) => {
                      const venueId = e.target.value;
                      setBroadcastFormData(prev => ({
                        ...prev,
                        audienceQuery: {
                          ...prev.audienceQuery,
                          filters: {
                            ...prev.audienceQuery.filters,
                            venueId: venueId || undefined
                          }
                        }
                      }));
                      // Clear activity when venue changes
                      if (venueId) {
                        setBroadcastFormData(prev => ({
                          ...prev,
                          audienceQuery: {
                            ...prev.audienceQuery,
                            filters: {
                              ...prev.audienceQuery.filters,
                              activityId: undefined
                            }
                          }
                        }));
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="">Select a venue</option>
                    {venues.map((venue) => (
                      <option key={venue.id} value={venue.id}>
                        {venue.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Activity Selection */}
              {broadcastFormData.audienceQuery.type === 'activity' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Venue (Optional - to filter activities)
                    </label>
                    <select
                      value={broadcastFormData.audienceQuery.filters?.venueId || ''}
                      onChange={async (e) => {
                        const venueId = e.target.value;
                        setBroadcastFormData(prev => ({
                          ...prev,
                          audienceQuery: {
                            ...prev.audienceQuery,
                            filters: {
                              ...prev.audienceQuery.filters,
                              venueId: venueId || undefined,
                              activityId: undefined // Clear activity when venue changes
                            }
                          }
                        }));
                        // Fetch activities for selected venue or all activities
                        try {
                          const token = authService.getToken();
                          if (!token) return;

                          const queryParams = new URLSearchParams();
                          if (venueId) {
                            queryParams.append('venue', venueId);
                          }

                          const response = await fetch(buildApiUrl(`/business/activities?${queryParams.toString()}`), {
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json',
                            }
                          });

                          if (response.ok) {
                            const data = await response.json();
                            if (data.success) {
                              setActivities((data.data.activities || []).map((a: any) => ({ id: a.id, title: a.name || a.title })));
                            }
                          }
                        } catch (error) {
                          console.error('Error fetching activities:', error);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">All Venues</option>
                      {venues.map((venue) => (
                        <option key={venue.id} value={venue.id}>
                          {venue.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Activity *
                    </label>
                    <select
                      value={broadcastFormData.audienceQuery.filters?.activityId || ''}
                      onChange={(e) => {
                        const activityId = e.target.value;
                        setBroadcastFormData(prev => ({
                          ...prev,
                          audienceQuery: {
                            ...prev.audienceQuery,
                            filters: {
                              ...prev.audienceQuery.filters,
                              activityId: activityId || undefined
                            }
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                      disabled={activities.length === 0}
                    >
                      <option value="">
                        {activities.length === 0
                          ? 'Loading activities...'
                          : 'Select an activity'}
                      </option>
                      {activities.map((activity) => (
                        <option key={activity.id} value={activity.id}>
                          {activity.title}
                        </option>
                      ))}
                    </select>
                    {activities.length === 0 && (
                      <p className="mt-1 text-sm text-gray-500">
                        {broadcastFormData.audienceQuery.filters?.venueId
                          ? 'No activities found for selected venue'
                          : 'Select a venue to filter activities, or activities will load automatically'}
                      </p>
                    )}
                  </div>
                </>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="sendNow"
                  checked={broadcastFormData.sendNow}
                  onChange={(e) => setBroadcastFormData(prev => ({ ...prev, sendNow: e.target.checked }))}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="sendNow" className="ml-2 text-sm text-gray-700">
                  Send immediately
                </label>
              </div>

              {!broadcastFormData.sendNow && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Schedule For
                  </label>
                  <input
                    type="datetime-local"
                    value={broadcastFormData.scheduledFor}
                    onChange={(e) => setBroadcastFormData(prev => ({ ...prev, scheduledFor: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required={!broadcastFormData.sendNow}
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateForm(false);
                    setBroadcastFormData({
                      title: '',
                      subject: '',
                      bodyHtml: '',
                      bodyText: '',
                      audienceQuery: {
                        type: 'all',
                        filters: {}
                      },
                      channels: ['email'],
                      scheduledFor: '',
                      sendNow: true
                    });
                  }}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving || !broadcastFormData.title || !broadcastFormData.subject || !broadcastFormData.bodyHtml || broadcastFormData.channels.length === 0}>
                  {saving ? 'Creating...' : 'Create Broadcast'}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Send Individual Email Form - Full Page */}
        {showIndividualEmail && (
          <Card className="p-6 mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Send Individual Email</h2>
              <Button
                variant="outline"
                onClick={() => {
                  setShowIndividualEmail(false);
                  setSelectedParent(null);
                  setManualEmailData({ email: '', name: '' });
                  setIndividualEmailData({
                    subject: '',
                    bodyHtml: '',
                    bodyText: ''
                  });
                  setParentSearchTerm('');
                  setEmailMode('parent');
                  setSelectedVenueId('');
                  setSelectedActivityId('');
                }}
              >
                <XMarkIcon className="h-5 w-5" />
                Close
              </Button>
            </div>

            <div className="space-y-6">
              {/* Email Mode Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Send Email To
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="emailMode"
                      value="parent"
                      checked={emailMode === 'parent'}
                      onChange={(e) => {
                        setEmailMode('parent');
                        setSelectedParent(null);
                        setManualEmailData({ email: '', name: '' });
                        setParentSearchTerm('');
                      }}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Parent (from system)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="emailMode"
                      value="manual"
                      checked={emailMode === 'manual'}
                      onChange={(e) => {
                        setEmailMode('manual');
                        setSelectedParent(null);
                        setParentSearchTerm('');
                      }}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Any Email Address</span>
                  </label>
                </div>
              </div>

              {/* Venue and Activity Filters */}
              {emailMode === 'parent' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Filter by Venue (Optional)
                    </label>
                    <select
                      value={selectedVenueId}
                      onChange={(e) => {
                        setSelectedVenueId(e.target.value);
                        setSelectedActivityId(''); // Clear activity when venue changes
                        setSelectedParent(null);
                        setParentSearchTerm('');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">All Venues</option>
                      {venues.map((venue) => (
                        <option key={venue.id} value={venue.id}>
                          {venue.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Filter by Activity (Optional)
                    </label>
                    <select
                      value={selectedActivityId}
                      onChange={(e) => {
                        setSelectedActivityId(e.target.value);
                        setSelectedParent(null);
                        setParentSearchTerm('');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      disabled={!selectedVenueId && venues.length > 0}
                    >
                      <option value="">All Activities</option>
                      {activities.map((activity) => (
                        <option key={activity.id} value={activity.id}>
                          {activity.title}
                        </option>
                      ))}
                    </select>
                    {!selectedVenueId && venues.length > 0 && (
                      <p className="mt-1 text-xs text-gray-500">Select a venue first to filter by activity</p>
                    )}
                  </div>
                </div>
              )}

              {/* Parent Search and Selection */}
              {emailMode === 'parent' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Parent by Name or Email *
                  </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={parentSearchTerm}
                    onChange={(e) => {
                      setParentSearchTerm(e.target.value);
                    }}
                    placeholder="Search by name or email..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                
                {/* Parent List */}
                {filteredParents.length > 0 && (
                  <div className="mt-2 border border-gray-200 rounded-md max-h-48 overflow-y-auto">
                    {filteredParents.map((parent) => (
                      <div
                        key={parent.id}
                        onClick={() => {
                          setSelectedParent({
                            id: parent.id,
                            email: parent.email,
                            fullName: parent.fullName
                          });
                          setParentSearchTerm(parent.fullName);
                        }}
                        className={`p-3 cursor-pointer hover:bg-gray-50 ${
                          selectedParent?.id === parent.id ? 'bg-green-50 border-l-4 border-green-500' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{parent.fullName}</p>
                            <p className="text-sm text-gray-500">{parent.email}</p>
                          </div>
                          {selectedParent?.id === parent.id && (
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {filteredParents.length === 0 && (
                  <div className="mt-2 p-4 text-center text-gray-500 border border-gray-200 rounded-md">
                    {parentSearchTerm 
                      ? `No parents found matching "${parentSearchTerm}"`
                      : (selectedVenueId || selectedActivityId 
                          ? 'No parents found with the selected filters'
                          : 'No parents available')
                    }
                  </div>
                )}

                {/* Selected Parent Display */}
                {selectedParent && (
                  <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <UserIcon className="h-6 w-6 text-green-600" />
                        <div>
                          <p className="font-medium text-gray-900">{selectedParent.fullName}</p>
                          <p className="text-sm text-gray-600">{selectedParent.email}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedParent(null);
                          setParentSearchTerm('');
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Manual Email Input */}
              {emailMode === 'manual' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={manualEmailData.email}
                      onChange={(e) => setManualEmailData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="recipient@example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recipient Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={manualEmailData.name}
                      onChange={(e) => setManualEmailData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Recipient's name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
              )}

              {/* Email Form */}
              {(emailMode === 'parent' ? selectedParent : manualEmailData.email) && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSendIndividualEmail();
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Subject *
                    </label>
                    <input
                      type="text"
                      value={individualEmailData.subject}
                      onChange={(e) => setIndividualEmailData(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Enter email subject"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Message (HTML) *
                    </label>
                    <textarea
                      value={individualEmailData.bodyHtml}
                      onChange={(e) => setIndividualEmailData(prev => ({ ...prev, bodyHtml: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      rows={10}
                      placeholder="Enter your email message (HTML supported)"
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      You can use HTML tags for formatting. Plain text version will be auto-generated.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plain Text Version (Optional)
                    </label>
                    <textarea
                      value={individualEmailData.bodyText}
                      onChange={(e) => setIndividualEmailData(prev => ({ ...prev, bodyText: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      rows={6}
                      placeholder="Optional: Plain text version (auto-generated if left empty)"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowIndividualEmail(false);
                        setSelectedParent(null);
                        setIndividualEmailData({
                          subject: '',
                          bodyHtml: '',
                          bodyText: ''
                        });
                        setParentSearchTerm('');
                      }}
                      disabled={sendingIndividualEmail}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={sendingIndividualEmail || !individualEmailData.subject || !individualEmailData.bodyHtml || (emailMode === 'manual' && !manualEmailData.email)}
                    >
                      {sendingIndividualEmail 
                        ? 'Sending...' 
                        : emailMode === 'parent' && selectedParent
                          ? `Send Email to ${selectedParent.fullName}`
                          : `Send Email to ${manualEmailData.email}`
                      }
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </Card>
        )}
      </div>
    </BusinessLayout>
  );
};

export default CommunicationsBroadcastsPage;
